import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  TestimonialsV5,
} from "../../App";
import { PillarAccent } from "../../components/atoms";
import { useContactPanel } from "../../components/ContactPanel";
import { api } from "../../admin/api";
import { useContent } from "../../lib/useContent";

/* ============================================================
   /showcase/:slug — Generic case-study template
   Variants:
     A — Monograph (Veronique-style, hero image + title block)
     B — Editorial spread (title overlaid, meta inline, shorter)
     C — Lookbook (split hero, softer grid, asymmetric)
   All variants share the same DNA; only hero composition and
   chapter rhythm differ so projects feel of-a-family.
============================================================ */

/* -----------------------------------------------------------
   Helpers
----------------------------------------------------------- */
const Title = ({ t, className = "" }) => (
  <span className={className}>
    {t.lead}
    {t.italic ? (
      <span className="italic font-normal text-neutral-400">{t.italic}</span>
    ) : null}
    {t.italic && t.accent ? " " : ""}
    {t.accent ? <PillarAccent>{t.accent}</PillarAccent> : null}
    {t.tail}
  </span>
);

const SectionLabel = ({ idx, label }) => (
  <div className="flex items-baseline gap-4 mb-7 md:mb-9">
    <span className="text-[0.65rem] md:text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
      {idx}
    </span>
    <span className="h-px flex-1 bg-white/[0.10] max-w-[80px]" />
    <span className="text-[0.65rem] md:text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
      {label}
    </span>
  </div>
);

const ChapterEyebrow = ({ n, label, total }) => (
  <div className="flex items-baseline gap-4">
    <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
      {n} / {String(total).padStart(2, "0")}
    </span>
    <span className="h-px flex-1 bg-white/[0.08] max-w-[60px]" />
    <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
      {label}
    </span>
  </div>
);

const ChapterTitle = ({ ch }) => (
  <h3 className="mt-6 font-display text-[1.7rem] md:text-[2.5rem] lg:text-[2.85rem] font-black tracking-[-0.03em] leading-[1.08] text-white">
    {ch.title}
    {ch.titleItalic ? (
      <span className="block italic font-normal text-neutral-300">
        {ch.titleItalic}
      </span>
    ) : null}
    {ch.titleAccent ? (
      <span className="block">
        <PillarAccent>{ch.titleAccent}</PillarAccent>
      </span>
    ) : null}
  </h3>
);

/* -----------------------------------------------------------
   HERO — Variant A (image first, title block below)
----------------------------------------------------------- */
const HeroA = ({ p, onPrimary }) => (
  <>
    <section
      data-testid="cs-hero-image"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div className="relative w-full h-[78svh] min-h-[36rem] max-h-[52rem]">
        <img
          src={p.heroImg}
          alt={p.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <span aria-hidden className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#0B0F14]/55 to-transparent" />
        <div className="absolute inset-x-0 top-0">
          <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20">
            <div className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-white/85">
              <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <Link to="/showcase" className="hover:text-[#F4D64E] transition-colors">Showcase</Link>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span className="text-[#F4D64E]">{p.name}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      data-testid="cs-hero"
      className="relative bg-[#0B0F14] border-b border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-end">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
              <span className="w-8 h-px bg-[#F4D64E]" />
              Case study · {p.n} / {p.total}
            </span>
            <h1
              data-testid="cs-hero-title"
              className="font-display mt-7 text-white leading-[0.9] tracking-[-0.04em] font-black text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[8.5rem] xl:text-[10rem]"
            >
              <span className="block">{p.nameLead}</span>
              {p.nameTail ? (
                <span className="block italic font-normal text-neutral-300">
                  {p.nameTail}
                  <span className="text-[#F4D64E]">.</span>
                </span>
              ) : (
                <span className="text-[#F4D64E]">.</span>
              )}
            </h1>
            <p className="mt-9 max-w-[55ch] text-[1.1rem] md:text-[1.3rem] text-neutral-200 leading-[1.55]">
              {p.tagline}
            </p>
          </div>
          <HeroMeta p={p} onPrimary={onPrimary} />
        </div>
      </div>
    </section>
  </>
);

/* -----------------------------------------------------------
   HERO — Variant B (title overlaid bottom-left on image,
   meta strip below on dark)
----------------------------------------------------------- */
const HeroB = ({ p, onPrimary }) => (
  <>
    <section
      data-testid="cs-hero-image"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div className="relative w-full h-[92svh] min-h-[40rem] max-h-[58rem]">
        <img
          src={p.heroImg}
          alt={p.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/85 via-[#0B0F14]/20 to-[#0B0F14]/55" />

        {/* Breadcrumb */}
        <div className="absolute inset-x-0 top-0">
          <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20">
            <div className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-white/85">
              <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <Link to="/showcase" className="hover:text-[#F4D64E] transition-colors">Showcase</Link>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span className="text-[#F4D64E]">{p.name}</span>
            </div>
          </div>
        </div>

        {/* Title overlay */}
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-14 md:pb-20">
            <span className="inline-flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
              <span className="w-8 h-px bg-[#F4D64E]" />
              Case study · {p.n} / {p.total}
            </span>
            <h1
              data-testid="cs-hero-title"
              className="font-display mt-7 text-white leading-[0.9] tracking-[-0.04em] font-black text-[3rem] sm:text-[4.5rem] md:text-[6.25rem] lg:text-[8rem] xl:text-[9.5rem] drop-shadow-[0_2px_32px_rgba(0,0,0,0.55)]"
            >
              <span className="block">{p.nameLead}</span>
              {p.nameTail ? (
                <span className="block italic font-normal text-white/85">
                  {p.nameTail}
                  <span className="text-[#F4D64E]">.</span>
                </span>
              ) : (
                <span className="text-[#F4D64E]">.</span>
              )}
            </h1>
          </div>
        </div>
      </div>
    </section>

    <section
      data-testid="cs-hero"
      className="relative bg-[#0B0F14] border-b border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-start">
          <div className="lg:col-span-7">
            <p className="max-w-[55ch] text-[1.1rem] md:text-[1.3rem] text-neutral-200 leading-[1.55]">
              {p.tagline}
            </p>
          </div>
          <HeroMeta p={p} onPrimary={onPrimary} compact />
        </div>
      </div>
    </section>
  </>
);

/* -----------------------------------------------------------
   HERO — Variant C (split: title left, portrait image right)
----------------------------------------------------------- */
const HeroC = ({ p, onPrimary }) => (
  <section
    data-testid="cs-hero"
    className="relative overflow-hidden bg-[#0B0F14] border-b border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-14 md:mb-20">
        <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <Link to="/showcase" className="hover:text-[#F4D64E] transition-colors">Showcase</Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">{p.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <span className="inline-flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
            <span className="w-8 h-px bg-[#F4D64E]" />
            Case study · {p.n} / {p.total}
          </span>
          <h1
            data-testid="cs-hero-title"
            className="font-display mt-7 text-white leading-[0.92] tracking-[-0.04em] font-black text-[3rem] sm:text-[4.5rem] md:text-[6.25rem] lg:text-[7.25rem]"
          >
            <span className="block">{p.nameLead}</span>
            {p.nameTail ? (
              <span className="block italic font-normal text-neutral-300">
                {p.nameTail}
                <span className="text-[#F4D64E]">.</span>
              </span>
            ) : (
              <span className="text-[#F4D64E]">.</span>
            )}
          </h1>
          <p className="mt-9 max-w-[52ch] text-[1.075rem] md:text-[1.2rem] text-neutral-300 leading-[1.6]">
            {p.tagline}
          </p>
          <HeroMeta p={p} onPrimary={onPrimary} inline />
        </div>

        {/* Portrait hero image */}
        <div className="lg:col-span-5 order-1 lg:order-2 relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/[0.08]">
          <img
            src={p.heroImg}
            alt={p.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/40 via-transparent to-transparent" />
        </div>
      </div>
    </div>
  </section>
);

/* -----------------------------------------------------------
   HERO META — core facts sidecar + CTA + live link
----------------------------------------------------------- */
const HeroMeta = ({ p, onPrimary, compact = false, inline = false }) => (
  <div className={inline ? "mt-12" : "lg:col-span-4"}>
    <dl
      className={`grid grid-cols-2 gap-y-7 gap-x-6 text-[0.78rem] tracking-[0.18em] uppercase font-bold ${
        inline ? "" : "border-t border-white/[0.12] pt-8"
      }`}
    >
      <dt className="text-neutral-500">Industry</dt>
      <dd className="text-white">{p.industry}</dd>

      <dt className="text-neutral-500">Year started</dt>
      <dd className="text-white tabular-nums">{p.year}</dd>

      <dt className="text-neutral-500">Status</dt>
      <dd className="text-[#F4D64E] flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
        {p.status}
      </dd>

      <dt className="text-neutral-500">Region</dt>
      <dd className="text-white">{p.region}</dd>
    </dl>

    {!compact ? (
      <div className="mt-9 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={onPrimary}
          data-testid="cs-hero-cta"
          className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-6 py-3.5 rounded-md transition-colors"
        >
          Start a project
          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
        {p.liveSite ? (
          <a
            href={p.liveSite}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
          >
            Visit live site
            <ArrowUpRight className="w-4 h-4" />
          </a>
        ) : null}
      </div>
    ) : null}
  </div>
);

/* -----------------------------------------------------------
   AT A GLANCE
----------------------------------------------------------- */
const AtAGlance = ({ p }) => (
  <section data-testid="cs-glance" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <SectionLabel idx="01" label="At a glance" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.025em] leading-[1.18] text-white">
            <Title t={p.atGlance.title} />
          </h2>
          <div className="mt-10 max-w-[58ch] text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] space-y-6">
            {p.atGlance.paras.map((para, i) => (
              <p key={i} dangerouslySetInnerHTML={{ __html: para }} />
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-white/[0.08]">
          <dl className="grid grid-cols-1 gap-y-6">
            {p.sidecar.map((item) => (
              <div key={item.k} className="flex items-baseline justify-between gap-6 pb-5 border-b border-white/[0.06]">
                <dt className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500 shrink-0">
                  {item.k}
                </dt>
                <dd className="text-[0.95rem] md:text-[1rem] text-white text-right">
                  {item.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  </section>
);

/* -----------------------------------------------------------
   THE BRIEF
----------------------------------------------------------- */
const TheBrief = ({ p }) => (
  <section data-testid="cs-brief" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-10">
        <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
          <SectionLabel idx="02" label="The brief" />
          <h2 className="font-display text-[2rem] md:text-[2.6rem] lg:text-[3.2rem] font-black tracking-[-0.03em] leading-[1.05] text-white">
            <Title t={p.brief.title} />
          </h2>
        </div>

        <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] max-w-[60ch]">
          {p.brief.paras.map((para, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: para }} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -----------------------------------------------------------
   CHAPTERS — one component per layout type
----------------------------------------------------------- */
const ChapterSplit = ({ ch, total, imageRight = false }) => (
  <article className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-10 items-center">
        <div className={`lg:col-span-7 relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.06] ${imageRight ? "lg:order-2" : ""}`}>
          <img src={ch.img} alt={ch.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/40 via-transparent to-transparent" />
        </div>
        <div className="lg:col-span-5">
          <ChapterEyebrow n={ch.n} label={ch.label} total={total} />
          <ChapterTitle ch={ch} />
          <p className="mt-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7] max-w-[52ch]">
            {ch.copy}
          </p>
        </div>
      </div>
    </div>
  </article>
);

const ChapterFullBleed = ({ ch, total }) => (
  <article className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="relative h-[60svh] min-h-[24rem] max-h-[40rem] overflow-hidden">
      <img src={ch.img} alt={ch.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/30 via-transparent to-[#0B0F14]/85" />
      <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/60 via-transparent to-transparent" />
    </div>
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-8">
        <div className="lg:col-span-5">
          <ChapterEyebrow n={ch.n} label={ch.label} total={total} />
          <ChapterTitle ch={ch} />
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-end">
          <p className="text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.7] max-w-[58ch]">
            {ch.copy}
          </p>
        </div>
      </div>
    </div>
  </article>
);

const ChapterDiptych = ({ ch, total }) => (
  <article className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <ChapterEyebrow n={ch.n} label={ch.label} total={total} />
      <h3 className="mt-7 font-display text-[2rem] sm:text-[2.6rem] md:text-[3.5rem] lg:text-[4rem] font-black tracking-[-0.035em] leading-[1.02] text-white max-w-5xl">
        {ch.title}
        {ch.titleItalic ? (
          <span className="italic font-normal text-neutral-400"> {ch.titleItalic}</span>
        ) : null}
        {ch.titleAccent ? (
          <>
            {" "}
            <PillarAccent>{ch.titleAccent}</PillarAccent>
          </>
        ) : null}
      </h3>
      <p className="mt-9 text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] max-w-[64ch]">
        {ch.copy}
      </p>
      <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06]">
          <img src={ch.imgA} alt={ch.captionA || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          {/* Bottom gradient ensures the caption stays legible on any
              image content (white-on-white was invisible before). */}
          <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-baseline justify-between text-[0.65rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">{ch.captionA}</span>
            <span className="text-[#F4D64E] drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">A</span>
          </div>
        </div>
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06] md:translate-y-12">
          <img src={ch.imgB} alt={ch.captionB || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-baseline justify-between text-[0.65rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">{ch.captionB}</span>
            <span className="text-[#F4D64E] drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">B</span>
          </div>
        </div>
      </div>
    </div>
  </article>
);

const ChapterGrid3 = ({ ch, total }) => (
  <article className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12 items-center">
        <div className="lg:col-span-5 lg:order-2">
          <ChapterEyebrow n={ch.n} label={ch.label} total={total} />
          <ChapterTitle ch={ch} />
          <p className="mt-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7] max-w-[52ch]">
            {ch.copy}
          </p>
        </div>
        <div className="lg:col-span-7 lg:order-1 grid grid-cols-2 grid-rows-2 gap-4 aspect-[5/4]">
          <div className="relative col-span-1 row-span-2 overflow-hidden rounded-2xl border border-white/[0.06]">
            <img src={ch.imgs[0]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
            <img src={ch.imgs[1]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
            <img src={ch.imgs[2]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </div>
  </article>
);

const ChapterVideo = ({ ch, total }) => (
  <article className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
      <div className="max-w-4xl">
        <ChapterEyebrow n={ch.n} label={ch.label} total={total} />
        <h3 className="mt-7 font-display text-[2rem] sm:text-[2.6rem] md:text-[3.5rem] lg:text-[4rem] font-black tracking-[-0.035em] leading-[1.02] text-white">
          {ch.title}
          {ch.titleItalic ? (
            <span className="italic font-normal text-neutral-400"> {ch.titleItalic}</span>
          ) : null}
          {ch.titleAccent ? (
            <>
              {" "}
              <PillarAccent>{ch.titleAccent}</PillarAccent>
            </>
          ) : null}
        </h3>
      </div>
    </div>
    <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[#0F141A]">
      <video
        src={ch.video}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={ch.poster}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/60 via-transparent to-transparent" />
      {ch.chip ? (
        <div className="absolute top-6 left-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-black/50 backdrop-blur-md border border-white/10 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
          {ch.chip}
        </div>
      ) : null}
    </div>
    {ch.columns ? (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {ch.columns.map((col, i) => (
            <div key={i}>
              <div className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] mb-3">{col.h}</div>
              <p className="text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[36ch]">
                {col.p}
              </p>
            </div>
          ))}
        </div>
      </div>
    ) : ch.copy ? (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <p className="max-w-[62ch] text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7]">
          {ch.copy}
        </p>
      </div>
    ) : null}
  </article>
);

const ChapterQuotePortrait = ({ ch, total }) => (
  <article className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12 items-stretch">
        <div className="lg:col-span-7 flex flex-col justify-between">
          <div>
            <ChapterEyebrow n={ch.n} label={ch.label2 || ch.label} total={total} />
            <blockquote className="mt-9 font-display text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] font-bold tracking-[-0.025em] leading-[1.15] text-white">
              &ldquo;{ch.quoteLead}
              {ch.quoteItalic ? (
                <span className="italic font-normal text-neutral-400"> {ch.quoteItalic}</span>
              ) : null}
              .&rdquo;
            </blockquote>
          </div>
          {ch.below ? (
            <div className="mt-12 lg:mt-0 pt-8 border-t border-white/[0.08] max-w-[52ch]">
              <p className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]">
                {ch.below}
              </p>
            </div>
          ) : null}
        </div>

        <div className="lg:col-span-5 relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/[0.06]">
          <img src={ch.portrait} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/50 via-transparent to-transparent" />
          <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex items-baseline justify-between text-[0.65rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">{ch.caption}</span>
            <span className="text-[#F4D64E] drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">{ch.tag}</span>
          </div>
        </div>
      </div>
    </div>
  </article>
);

const renderChapter = (ch, total) => {
  switch (ch.layout) {
    case "split-image-right":
      return <ChapterSplit key={ch.n} ch={ch} total={total} imageRight />;
    case "full-bleed":
    case "full-bleed-video": // treat video as video layout below
      if (ch.layout === "full-bleed-video") return <ChapterVideo key={ch.n} ch={ch} total={total} />;
      return <ChapterFullBleed key={ch.n} ch={ch} total={total} />;
    case "diptych":
      return <ChapterDiptych key={ch.n} ch={ch} total={total} />;
    case "grid-3":
      return <ChapterGrid3 key={ch.n} ch={ch} total={total} />;
    case "video":
      return <ChapterVideo key={ch.n} ch={ch} total={total} />;
    case "quote-portrait":
      return <ChapterQuotePortrait key={ch.n} ch={ch} total={total} />;
    case "split-image-left":
    default:
      return <ChapterSplit key={ch.n} ch={ch} total={total} />;
  }
};

const WhatWeShipped = ({ p }) => (
  <section data-testid="cs-shipped" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
      <div className="max-w-4xl">
        <SectionLabel idx="03" label="What we shipped" />
        <h2 className="font-display text-[2.5rem] md:text-[3.75rem] lg:text-[5rem] font-black tracking-[-0.04em] leading-[0.96] text-white">
          <Title t={p.shippedTitle} />
        </h2>
      </div>
    </div>
    <div className="mt-12">
      {p.chapters.map((ch) => renderChapter(ch, p.chapters.length))}
    </div>
  </section>
);

/* -----------------------------------------------------------
   BY THE NUMBERS
----------------------------------------------------------- */
const ByTheNumbers = ({ p }) => (
  <section data-testid="cs-numbers" className="relative bg-[#0F141A] border-y border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <SectionLabel idx="04" label="By the numbers" />
      <ul className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8 mt-2">
        {p.numbers.map((n) => (
          <li key={n.label} className="flex flex-col gap-3">
            <span className="font-display text-[2.5rem] md:text-[3.5rem] lg:text-[4.5rem] font-black tracking-[-0.04em] leading-[0.92] text-white">
              {n.v.includes(" · ") ? (
                <span>
                  <span>{n.v.split(" · ")[0]}</span>
                  <span className="text-[#F4D64E] mx-1.5">·</span>
                  <span>{n.v.split(" · ")[1]}</span>
                </span>
              ) : (
                <span>{n.v}</span>
              )}
            </span>
            <span className="text-[0.7rem] md:text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-500 max-w-[24ch]">
              {n.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* -----------------------------------------------------------
   IN THE PRESS (optional)
----------------------------------------------------------- */
const InThePress = ({ p }) => {
  // Site-wide headline override from CMS (home → press_block).
  // Empty fields keep the current hardcoded copy so existing pages
  // are unaffected.
  const home = useContent("home");
  const pb = (home && home.press_block) || {};
  const eyebrow = pb.eyebrow || "In the press";
  const headlineMain = pb.headline_main || "The work showed up";
  const headlineItalic = pb.headline_italic || "where it";
  const headlineAccent = pb.headline_accent || "matters";
  return (
    <section data-testid="cs-press" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <SectionLabel idx="05" label={eyebrow} />
        <h2 className="font-display text-[2.25rem] md:text-[3rem] lg:text-[3.75rem] font-black tracking-[-0.035em] leading-[1.05] text-white max-w-3xl">
          <span>
            {headlineMain}
            {headlineItalic ? (
              <>
                {" "}
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
              </>
            ) : null}
            {headlineAccent ? (
              <>
                {" "}
                <PillarAccent>{headlineAccent}</PillarAccent>
              </>
            ) : null}
            .
          </span>
        </h2>
        <ul className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {p.press.map((item, i) => (
            <li
              key={item.pub}
              className="relative bg-[#0F141A] border border-white/[0.06] rounded-2xl p-7 md:p-9 hover:border-[#F4D64E]/30 transition-colors"
            >
              <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <blockquote
                className="mt-7 font-display text-[1.05rem] md:text-[1.15rem] lg:text-[1.2rem] font-bold tracking-[-0.005em] text-white leading-[1.45]"
                dangerouslySetInnerHTML={{ __html: `&ldquo;${item.quote}&rdquo;` }}
              />
              <div className="mt-7 pt-5 border-t border-white/[0.08] flex items-center justify-between">
                <span className="font-display text-[1.4rem] md:text-[1.6rem] font-black tracking-[-0.015em] uppercase text-white">
                  {item.pub}
                </span>
                <span className="text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                  Editorial coverage
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   OUTCOME
----------------------------------------------------------- */
const Outcome = ({ p, idx }) => (
  <section data-testid="cs-outcome" className="relative bg-[#0B0F14] border-t border-white/[0.06] overflow-hidden">
    <span aria-hidden className="absolute -top-32 -right-24 w-[40rem] h-[40rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
      <SectionLabel idx={idx} label="Where it stands" />
      <div className="max-w-5xl">
        <p className="font-display text-[2rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.5rem] font-black tracking-[-0.035em] leading-[1.05] text-white">
          <Title t={p.outcome.title} />
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl">
          {p.outcome.paras.map((para, i) => (
            <p
              key={i}
              className="text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7]"
              dangerouslySetInnerHTML={{ __html: para }}
            />
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -----------------------------------------------------------
   NEXT PROJECT
----------------------------------------------------------- */
const NextProject = ({ p, onContact }) => (
  <section data-testid="cs-next" className="relative bg-[#0F141A] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-center">
        <div className="lg:col-span-7">
          <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
            Next case study · {p.next.n} / {p.total}
          </span>
          <Link
            to={`/showcase/${p.next.slug}`}
            data-testid="cs-next-link"
            className="group block mt-6"
          >
            <div className="font-display text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.5rem] font-black tracking-[-0.04em] leading-[0.96] text-white group-hover:text-[#F4D64E] transition-colors">
              {p.next.name}
            </div>
            <div className="mt-4 inline-flex items-center gap-3 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] group-hover:text-white transition-colors">
              {p.next.meta}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>

        <div className="lg:col-span-5 lg:text-right">
          <p className="text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[40ch] lg:ml-auto">
            Ready to start your own chapter?
          </p>
          <button
            type="button"
            onClick={onContact}
            data-testid="cs-next-cta"
            className="group mt-6 inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
          >
            Let&apos;s talk
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   PAGE
============================================================ */
export default function CaseStudyPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null); // null = loading, false = 404
  // Computed Next-project override (slug/name/meta/n/total) built
  // from the live ordered published list. Skips deleted/unpublished
  // projects and wraps from the last item back to the first.
  // If the list fetch fails, we fall back to the doc's stored `next`.
  const [nextOverride, setNextOverride] = useState(null);
  const { open: openContact } = useContactPanel();

  useEffect(() => {
    let cancelled = false;
    setP(null);
    setNextOverride(null);

    const base = process.env.REACT_APP_BACKEND_URL || "";
    Promise.all([
      api.showcaseGet(slug),
      fetch(`${base}/api/showcase?public=true`)
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
    ])
      .then(([doc, list]) => {
        if (cancelled) return;
        if (!doc || doc.published === false) {
          setP(false);
          return;
        }
        setP(doc);
        document.title = `${doc.name} — Sunday Digital Showcase`;

        if (Array.isArray(list) && list.length > 0) {
          const i = list.findIndex((d) => d && d.slug === slug);
          if (i !== -1) {
            const total = list.length;
            // Wrap to first when on the last project.
            const nextIdx = (i + 1) % total;
            const nextDoc = list[nextIdx];
            // Match stored `meta` formatting (industry already
            // contains the ` · ` separator e.g. "Food · Mission").
            const metaParts = [];
            if (nextDoc.industry) metaParts.push(nextDoc.industry);
            if (nextDoc.year) metaParts.push(String(nextDoc.year));
            setNextOverride({
              slug: nextDoc.slug,
              name: nextDoc.name,
              meta: metaParts.join(" · "),
              // 1-based position in the live published list, 02-padded.
              n: String(nextIdx + 1).padStart(2, "0"),
              total: String(total).padStart(2, "0"),
            });
          }
        }
      })
      .catch(() => {
        if (!cancelled) setP(false);
      });
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (p === null) {
    return (
      <div className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body" data-variant="v5">
        <Nav />
        <section className="bg-[#0B0F14] py-32 text-center">
          <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
            Loading…
          </span>
        </section>
        <Footer />
      </div>
    );
  }
  if (p === false) {
    return <Navigate to="/showcase" replace />;
  }

  const Hero = p.variant === "B" ? HeroB : p.variant === "C" ? HeroC : HeroA;
  const showPress = Array.isArray(p.press) && p.press.length > 0;
  const outcomeIdx = showPress ? "06" : "05";

  return (
    <div
      data-testid={`cs-page-${p.slug}`}
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <Hero p={p} onPrimary={openContact} />
        <TestimonialsV5 contentKey="case_study" slug={slug} />
        <AtAGlance p={p} />
        <TheBrief p={p} />
        <WhatWeShipped p={p} />
        <ByTheNumbers p={p} />
        {showPress ? <InThePress p={p} /> : null}
        <Outcome p={p} idx={outcomeIdx} />
        <NextProject
          p={
            nextOverride
              ? { ...p, next: nextOverride, total: nextOverride.total }
              : p
          }
          onContact={openContact}
        />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}

/* Silence unused-import warning in environments that tree-shake oddly */
